import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { formatPayout } from "@/lib/utils";

export default function GigDetailsModal({
  isClient,
  activeGig,
  activeCity,
  activePayout,
  activeGenres,
  activeTags,
  activeDescription,
  activeVenueName,
  activeContactName,
  activeContactEmail,
  activeContactPhone,
  activeWebsite,
  mapsUrl,
  modalAvailabilityClass,
  modalAvailabilityLabel,
  modalAvailabilityDisabled,
  modalShowInterestedButton,
  modalCanRespond,
  onClose,
  onToggleAvailability,
  onOpenInterestedModal,
  onRequestAuth,
}) {
  if (!isClient || !activeGig) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex justify-end"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
      <aside
        className="relative flex h-full w-full max-w-md flex-col bg-zinc-50 shadow-2xl dark:bg-zinc-950"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-zinc-200/60 p-6 dark:border-white/[0.07]">
          <div className="min-w-0 pr-4">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-400 dark:text-zinc-500">
              Gig details
            </p>
            <h2 className="mt-1 text-xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
              {activeGig.title}
            </h2>
            {activeVenueName && (
              <p className="mt-0.5 text-sm text-zinc-500 dark:text-zinc-400">
                {activeVenueName}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg border border-zinc-200/60 p-2 text-zinc-400 transition hover:text-zinc-600 dark:border-white/[0.07] dark:hover:text-zinc-200"
            aria-label="Close details"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M1 1l12 12M13 1L1 13"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 space-y-6 overflow-y-auto p-6">
          {/* Date / City / Payout */}
          <section className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-zinc-700 dark:text-zinc-300">
                {activeGig.date
                  ? new Date(activeGig.date).toLocaleDateString(undefined, { dateStyle: "medium" })
                  : "Date to be announced"}
              </span>
              {activeCity && (
                <span className="inline-flex items-center rounded-md bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-600 dark:bg-white/[0.07] dark:text-zinc-300">
                  {activeCity}
                </span>
              )}
            </div>
            {Number.isFinite(activePayout) && (
              <div className="inline-flex items-center rounded-md bg-amber-50 px-2.5 py-1 text-sm font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-300">
                {formatPayout(activePayout)}
              </div>
            )}
          </section>

          {activeDescription && (
            <section>
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">
                Description
              </h3>
              <p className="mt-2 whitespace-pre-line text-sm text-zinc-700 dark:text-zinc-300">
                {activeDescription}
              </p>
            </section>
          )}

          {activeGenres.length > 0 && (
            <section>
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">
                Genres
              </h3>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {activeGenres.map((genre) => (
                  <span
                    key={`active-genre-${genre}`}
                    className="inline-flex items-center rounded-md bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                  >
                    {genre}
                  </span>
                ))}
              </div>
            </section>
          )}

          {activeTags.length > 0 && (
            <section>
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">
                Tags
              </h3>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {activeTags.map((tag) => (
                  <span
                    key={`active-tag-${tag}`}
                    className="inline-flex items-center rounded-md border border-zinc-200 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-zinc-500 dark:border-white/[0.08] dark:text-zinc-400"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </section>
          )}

          {(activeContactName || activeContactEmail || activeContactPhone || activeWebsite) && (
            <section className="space-y-1.5">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-400 dark:text-zinc-500">
                Contact
              </h3>
              <div className="space-y-1 text-sm text-zinc-700 dark:text-zinc-300">
                {activeContactName && <p>{activeContactName}</p>}
                {activeContactEmail && (
                  <a
                    className="block text-amber-600 hover:underline dark:text-amber-400"
                    href={`mailto:${activeContactEmail}`}
                  >
                    {activeContactEmail}
                  </a>
                )}
                {activeContactPhone && <p>{activeContactPhone}</p>}
                {activeWebsite && (
                  <a
                    className="text-amber-600 hover:underline dark:text-amber-400"
                    href={activeWebsite}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Visit website →
                  </a>
                )}
              </div>
            </section>
          )}

          {mapsUrl && (
            <section>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200/60 px-3 py-2 text-sm font-medium text-zinc-600 transition hover:border-zinc-300 hover:text-zinc-900 dark:border-white/[0.07] dark:text-zinc-300 dark:hover:text-zinc-100"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
                  <circle cx="12" cy="9" r="2.5"/>
                </svg>
                View on Google Maps
              </a>
            </section>
          )}
        </div>

        {/* Footer CTA */}
        <div className="border-t border-zinc-200/60 p-6 dark:border-white/[0.07]">
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              className={`w-full ${modalAvailabilityClass}`}
              onClick={() => {
                if (modalShowInterestedButton) { onOpenInterestedModal(activeGig); return; }
                if (!modalCanRespond) { onRequestAuth?.(); return; }
                onToggleAvailability(activeGig.id);
              }}
              disabled={modalAvailabilityDisabled && modalCanRespond}
            >
              {modalAvailabilityLabel}
            </Button>
            <Button
              type="button"
              className="w-full btn-secondary"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </aside>
    </div>,
    document.body
  );
}
